/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { ButtonSpinner } from '../_common/ButtonSpinner';
import { type PolymorphicCommonProps } from '../_common/types';
import { ChevronDownIconOutline } from '../icons';

/**
 *
 *
 * Types
 *
 *
 */

type DropdownButtonProps = {
  /** Content displayed in the button */
  children?: React.ReactNode;
  /** Size of button */
  size?: 'small' | 'medium' | 'large';
  /** Whether the button is in floating state */
  isFloating?: boolean;
  /** Whether the button is in disabled state */
  isDisabled?: boolean;
  /** Element that is shown before the button content */
  leadingElement?: React.ReactNode;
  /** If the dropdown is activated */
  isOpen?: boolean;
  /** Callback function to be called when the button is clicked */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Additional classnames */
  className?: string;
  /** If the button is loading. Will display a loading spinner*/
  isLoading?: boolean;
  /** Accessible message for screen readers when the button is in a loading state */
  loadingMessage?: string;
};

/**
 *
 *
 * DropdownButton Component
 *
 *
 */

export const DropdownButton = function DropdownButton<
  T extends React.ElementType = 'button',
>({
  as,
  children,
  size = 'medium',
  isDisabled = false,
  isFloating = false,
  leadingElement,
  isOpen = false,
  onClick,
  className,
  style,
  htmlAttributes,
  ref,
  isLoading = false,
  loadingMessage = 'Loading',
  ...restProps
}: PolymorphicCommonProps<T, DropdownButtonProps>) {
  const Component: React.ElementType = as ?? 'button';
  const classes = classNames('ndl-dropdown-btn', className, {
    'ndl-disabled': isDisabled,
    'ndl-floating-btn': isFloating,
    'ndl-large': size === 'large',
    'ndl-loading': isLoading,
    'ndl-medium': size === 'medium',
    'ndl-open': isOpen,
    'ndl-small': size === 'small',
  });

  const isInteractable = !isDisabled && !isLoading;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // If the button is disabled or loading, we don't want to call the onClick handler. The loading is only aria-disabled, so we need this.
    if (!isInteractable) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Component
      className={classes}
      disabled={isDisabled}
      aria-disabled={!isInteractable}
      onClick={handleClick}
      aria-expanded={isOpen}
      ref={ref}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-dropdown-btn-inner">
        <div className="ndl-dropdown-btn-leading-wrapper">
          {leadingElement}
          <span className="ndl-dropdown-btn-content">{children}</span>
        </div>

        <ChevronDownIconOutline
          className={classNames('ndl-dropdown-button-icon', {
            'ndl-dropdown-button-icon-open': isOpen,
          })}
        />
        {isLoading && (
          <ButtonSpinner loadingMessage={loadingMessage} size={size} />
        )}
      </div>
    </Component>
  );
};
